import React, {createContext, useContext, useEffect, useMemo} from 'react';
import {useSelector} from 'react-redux';

import {STATSIG_GATES} from 'src/contexts/statsig-contex';
import {selectAccessRights} from 'src/hooks/access-rights/services/access-rights-slice';
import {useProducts} from 'src/hooks/dictionaries/useProducts';

import {FlatButton} from '@pb/kit-component-button';
import {Message} from '@pb/kit-component-message';
import {useGateValue} from '@pb/kit-lib-statsig';

import {buildProductColumns} from './product-columns';

import type {ProductSummary} from 'src/openapi/admin/models/ProductSummary';

type Catalogue = {
    products: ProductSummary[],
    ready: boolean,
    refreshVersion: number,
};

const ProductColumnsContext = createContext<Catalogue>({
    products: [], ready: true, refreshVersion: 0,
});

export const useProductColumns = () => {
    const catalogue = useContext(ProductColumnsContext);
    const productColumns = useMemo(() => buildProductColumns(catalogue.products), [catalogue.products]);
    const columnMap = useMemo(() => (
        Object.fromEntries(productColumns.map(({code}) => [code, code]))
    ), [productColumns]);
    const headers = useMemo(() => (
        Object.fromEntries(productColumns.map(({code, name}) => [code, name]))
    ), [productColumns]);
    return {...catalogue, productColumns, columnMap, headers};
};

type Props = {children: React.ReactNode, refreshVersion: number};

export const ProductColumnsProvider = ({children, refreshVersion}: Props) => {
    const provisioningHistoryEnabled = useGateValue(STATSIG_GATES.ACCOUNT_PROVISIONING_HISTORY);
    const rights = useSelector(selectAccessRights);
    const allowed = provisioningHistoryEnabled && !!rights?.includes('USER_PRODUCTS_VIEW');
    const {products, status, error, loadProducts} = useProducts();

    useEffect(() => {
        if (allowed) {
            loadProducts();
        }
    }, [allowed, loadProducts]);

    const value = useMemo(() => ({
        products: allowed ? products : [],
        ready: !allowed || status === 'succeeded',
        refreshVersion,
    }), [allowed, products, status, refreshVersion]);

    return (
        <ProductColumnsContext.Provider value={value}>
            {allowed && error ? (
                <Message color="error">
                    {error.message || 'Failed to load products'}
                    <FlatButton onClick={loadProducts}>Retry</FlatButton>
                </Message>
            ) : null}
            {children}
        </ProductColumnsContext.Provider>
    );
};
